import {
  TeBadge,
  TeButton,
  TeContainer,
  TeContainerSlim,
  TeIcon,
  TeText,
} from '@topcon/tuicl-react';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@topcon/topcon-utils';

import { useCreateRequestToJoin } from '../Companies/companies.mutations';
import {
  BusinessPartnerWithCurrentIdentityInfoBody,
  RequestToJoinPostBody,
} from '../../api/generated/pap';
import CompanyIcon from '../../components/Company/CompanyIcon';
import { useErrorDrawer } from '../../providers/ErrorDrawerContext/error-drawer.context';

interface Props {
  divisions: BusinessPartnerWithCurrentIdentityInfoBody[];
  userId?: string;
  isOverview?: boolean;
  refetch?: () => void;
}

function RenderDivisions({ divisions, userId, isOverview = false, refetch }: Props) {
  const { t } = useTranslation(['divisions', 'profile', 'common']);
  const { mutateAsync: requestToJoin } = useCreateRequestToJoin();
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();

  const joinRequest = (businessPartnerId: string, name: string) => {
    requestToJoin({
      businessPartnerId,
    } as RequestToJoinPostBody)
      .then(() => {
        refetch?.();
      })
      .catch((error: unknown) => {
        if (!showError(error)) {
          notificationService.addNotification(
            t('requestToJoinFailed', { ns: 'common' }),
            t('requestToJoinFailedDescription', { ns: 'common', name }),
            'error'
          );
        }
      });
  };

  const renderProfiles = (division: BusinessPartnerWithCurrentIdentityInfoBody) => {
    return division.user?.profileIds.map((profileId) => {
      return (
        <TeBadge
          key={profileId}
          size="s"
          backgroundColor="background.primary"
          textColor="text.primary"
          text={t(profileId, { ns: 'profile' })}
        />
      );
    });
  };

  const renderStatus = (division: BusinessPartnerWithCurrentIdentityInfoBody) => {
    if (division.requestToJoin) {
      return (
        <TeBadge
          size="s"
          backgroundColor="background.warning.primary"
          textColor="text.warning.primary"
          text={t('requestedToJoin', { ns: 'common' })}
        />
      );
    } else if (!division.isOwner && userId !== division?.ownerIdentityId) {
      return (
        <TeText size={12} color="text.secondary">
          {division.accessability === 'inviteOnly'
            ? t('inviteOnly')
            : t('notJoined', { ns: 'common' })}
        </TeText>
      );
    }
  };

  return (
    <>
      {divisions &&
        divisions.map((division) => {
          return (
            <StyledContainer
              key={division.id}
              grow
              borderRadius={8}
              backgroundColor="background.secondary"
              alignItems="center"
              data-testid={`division-card-${division.id}`}
            >
              <CompanyIcon iconURL={division.iconURL} size="xl" />
              <TeContainerSlim column gap="xs" grow>
                <TeContainerSlim gap="xs2" alignItems="center">
                  {division.accessability === 'inviteOnly' && <TeIcon name="lock" />}
                  <TeText size={16} weight={500}>
                    {division.name}
                  </TeText>
                </TeContainerSlim>

                {userId === division.ownerIdentityId && (
                  <TeBadge size="s" backgroundColor="background.inverse" text={t('owner')} />
                )}
                {userId === division?.user?.identityId && division.user
                  ? renderProfiles(division)
                  : renderStatus(division)}
              </TeContainerSlim>
              {!isOverview &&
                !division.isOwner &&
                userId !== division?.ownerIdentityId &&
                userId !== division?.user?.identityId &&
                userId !== division?.requestToJoin?.identityId && (
                  <TeButton
                    variant="neutral"
                    size="s"
                    text={t('requestToJoin', { ns: 'common' })}
                    teclick={() => joinRequest(division.id, division.name)}
                  />
                )}
            </StyledContainer>
          );
        })}
    </>
  );
}

// TODO: Should Container grow inside of Grid?
const StyledContainer = styled(TeContainer)`
  min-width: 100% !important;
  width: 100% !important;
`;

export default RenderDivisions;
